import type {Model} from '@github-ui/marketplace-common'
import {modelPlaygroundPath, modelPromptPath, repoModelPlaygroundPath, type Repository} from '@github-ui/paths'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {StackIcon, XIcon} from '@primer/octicons-react'
import {IconButton, LinkButton, useResponsiveValue} from '@primer/react'
import {useCallback, useMemo, useRef, useState} from 'react'
import {GiveFeedback} from '../../../components/GiveFeedback'
import {UseThisModelButton} from '../../../components/UseThisModelButton'
import {usePlaygroundState} from '../../../contexts/PlaygroundStateContext'
import type {GettingStarted, SidebarSelectionOptions} from '../../../types'
import type {ModelPersistentUIState} from '../../../utils/playground-local-storage'
import {Panel} from '../../../utils/playground-manager'
import {templateRepositoryNwo} from '../../../utils/playground-types'
import GettingStartedDialog from './GettingStartedDialog/GettingStartedDialog'
import ModelSwitcher from './ModelSwitcher'
import {PresetsMenu} from './presets/PresetsMenu'
import {useParams} from 'react-router-dom'

export function PlaygroundHeader({
  model,
  position,
  isOnMarketplace,
  gettingStarted,
  uiState,
  setUiState,
  handleSetSidebarTab,
}: {
  model: Model
  position: number
  isOnMarketplace: boolean
  gettingStarted: GettingStarted
  uiState: ModelPersistentUIState
  setUiState: (uiState: ModelPersistentUIState) => void
  handleSetSidebarTab: (newTab: SidebarSelectionOptions) => void
}) {
  const {owner, repo} = useParams<{
    owner: string
    repo: string
  }>()
  const openInCodespacesUrl = `/codespaces/new?template_repository=${templateRepositoryNwo}`
  const [isGettingStartedDialogOpen, setGettingStartedDialogOpen] = useState(false)
  const useThisModelRef = useRef(null)
  const {models = []} = usePlaygroundState()
  const onComparisonMode = models.length > 1
  const isMobile = useResponsiveValue({narrow: true}, false) as boolean
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const featureFlags = useFeatureFlags()

  const repository = useMemo(() => ({name: repo, ownerLogin: owner}) as Repository, [repo, owner])

  const onCloseModel = () => {
    if (position === Panel.Main) {
      // If we're removing the main model, we need to navigate to the new main model
      navigate({
        pathname: isOnMarketplace
          ? modelPlaygroundPath(models[Panel.Side]?.catalogData || model)
          : repoModelPlaygroundPath(repository, models[Panel.Side]?.catalogData || model),
        search: `retain=${Panel.Side}`,
      })
    } else {
      // If we're removing the side model, we just remove the compare_to
      setSearchParams()
    }
  }

  const onModelSelect = useCallback(
    async (m: Model) => {
      searchParams.delete('resend-user-prompt')
      if (position === Panel.Side) {
        setSearchParams({
          compare_to: m.name,
        })
      } else {
        navigate({
          pathname: isOnMarketplace ? modelPlaygroundPath(m) : repoModelPlaygroundPath(repository, m),
          search: searchParams.toString(),
        })
      }
    },
    [searchParams, position, setSearchParams, navigate, isOnMarketplace, repository],
  )

  const handleAddModel = async (m: Model) => {
    const modelName = m.name
    if (!modelName) return

    setSearchParams({
      compare_to: modelName,
    })
  }

  const defaultClasses = 'd-flex flex-row flex-justify-between flex-items-center gap-3'
  const comparisonClasses = 'p-2 border border-bottom-0 rounded-top-2'
  const individualClasses = isMobile ? 'pb-2' : 'pb-3'
  const showPresets = !isMobile && isOnMarketplace

  return (
    <div className={`${defaultClasses} ${onComparisonMode ? comparisonClasses : individualClasses}`}>
      <div className="d-flex flex-items-center gap-2">
        <ModelSwitcher
          model={model}
          onSelect={onModelSelect}
          onComparisonMode={onComparisonMode}
          handleSetSidebarTab={handleSetSidebarTab}
        />
        {!onComparisonMode && !isMobile && (
          <ModelSwitcher
            model={model}
            onSelect={handleAddModel}
            onComparisonMode={false}
            handleSetSidebarTab={handleSetSidebarTab}
            variant="compare_button"
          />
        )}
      </div>
      <div className="d-flex flex-items-center gap-2">
        {!onComparisonMode && (
          <>
            {!isMobile && isOnMarketplace && <GiveFeedback playground />}
            {featureFlags.github_models_prompt_editor && (
              <LinkButton
                className={isMobile ? 'px-2' : ''}
                aria-label="Prompt editor"
                leadingVisual={StackIcon}
                href={modelPromptPath(model)}
              >
                {isMobile ? undefined : 'Prompt editor'}
              </LinkButton>
            )}
            {showPresets && <PresetsMenu />}
          </>
        )}

        <UseThisModelButton
          className={isMobile ? 'px-2' : ''}
          variant="primary"
          model={model}
          ref={useThisModelRef}
          onClick={() => setGettingStartedDialogOpen(true)}
        />
        {onComparisonMode && <IconButton icon={XIcon} aria-label="Close model" onClick={onCloseModel} />}
        {isGettingStartedDialogOpen && (
          <GettingStartedDialog
            openInCodespaceUrl={openInCodespacesUrl}
            onClose={() => setGettingStartedDialogOpen(false)}
            showCodespacesSuggestion
            gettingStarted={gettingStarted}
            modelName={model.name}
            uiState={uiState}
            setUiState={setUiState}
            returnFocusRef={useThisModelRef}
          />
        )}
      </div>
    </div>
  )
}

try{ PlaygroundHeader.displayName ||= 'PlaygroundHeader' } catch {}